import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  toChatMessage,
  useConversationTurn,
} from '@gasbi/shared/common/components/AgentChat';
import type { ChatMessage } from '@gasbi/shared/common/components/Chat';
import type {
  AgentReadModel,
  StreamMessage,
} from '@gasbi/shared/common/features/agents';
import { useDispatch } from 'react-redux';

import {
  agentsApi,
  streamMessage,
  useCreateChatConversationMutation,
  useGetConversationQuery,
} from '@/features/agents/agentsApi';

const AGENT_NOT_IN_APP = 'Conversations.AgentNotInApp';

type Options = {
  /** The agents the user may start a conversation with here. */
  agents: AgentReadModel[];
  /** The panel's app: a new conversation is started in it (ADR-0004 §2.8); the full page has none. */
  appKey?: string;
  /** Nothing is fetched until the session (and, in the panel, the host app) is ready. */
  enabled: boolean;
  /** After every assistant answer that completed: the panel tells the launcher (`unread`). */
  onAnswered?: () => void;
};

const errorCodeOf = (error: unknown) =>
  (error as { data?: { errors?: { errorCode?: string }[] } } | null)?.data
    ?.errors?.[0]?.errorCode;

/**
 * One conversation of the chat, as the CRM page and the Admin test drawer run it: the stored messages come from the
 * agents service, the running turn from `useConversationTurn`, and the two are merged until the refetch holds the
 * turn. The first message creates the conversation with the chosen agent, in the panel's app when there is one.
 */
export const useAgentChat = ({
  agents,
  appKey,
  enabled,
  onAnswered,
}: Options) => {
  const dispatch = useDispatch();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [chosenAgentId, setChosenAgentId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const conversationQuery = useGetConversationQuery(conversationId ?? '', {
    skip: !conversationId || !enabled,
  });
  const [createConversation] = useCreateChatConversationMutation();

  const onAnsweredRef = useRef(onAnswered);
  useEffect(() => {
    onAnsweredRef.current = onAnswered;
  }, [onAnswered]);

  const streamAndNotify = useMemo<StreamMessage>(
    () =>
      async function* (request, options) {
        for await (const event of streamMessage(request, options)) {
          yield event;
          if (event.type === 'done' && event.status === 'completed')
            onAnsweredRef.current?.();
        }
      },
    [],
  );

  const onFinished = useCallback(
    (id: string) => {
      dispatch(
        agentsApi.util.invalidateTags([
          { type: 'Conversation', id },
          'Conversations',
        ]),
      );
    },
    [dispatch],
  );
  const turn = useConversationTurn({
    streamMessage: streamAndNotify,
    onFinished,
  });
  const { cancel, clear } = turn;

  const conversation = conversationId ? conversationQuery.data : undefined;
  const fetched = useMemo(
    () => (conversation?.messages ?? []).map(toChatMessage),
    [conversation],
  );

  // Once the refetched conversation holds the streamed turn, the local copies step aside.
  useEffect(() => {
    if (
      !turn.isStreaming &&
      turn.message &&
      fetched.some(m => m.id === turn.message?.id)
    )
      clear();
  }, [fetched, turn.isStreaming, turn.message, clear]);

  // The local copies of the turn stay until the refetch brings the stored rows; the user's text is not shown twice.
  const messages = useMemo<ChatMessage[]>(() => {
    const extra: ChatMessage[] = [];
    const lastUser = [...fetched].reverse().find(m => m.role === 'user');
    if (turn.pendingUser && lastUser?.text !== turn.pendingUser.text)
      extra.push(turn.pendingUser);
    if (
      !turn.isStreaming &&
      turn.message &&
      !fetched.some(m => m.id === turn.message?.id)
    )
      extra.push(turn.message);
    return [...fetched, ...extra];
  }, [fetched, turn.pendingUser, turn.message, turn.isStreaming]);

  // The agent of the open conversation; a new one takes the chosen agent, else the first one.
  const agentId = conversation?.agentId ?? chosenAgentId;
  const agent =
    agents.find(a => a.id === agentId) ??
    (conversationId ? undefined : agents[0]);

  /** Shows a stored conversation, or a new one for `null`; the running turn is dropped. */
  const open = useCallback(
    (id: string | null) => {
      cancel();
      clear();
      setStartError(null);
      setConversationId(id);
    },
    [cancel, clear],
  );

  /** Another agent than the open conversation's starts a new conversation with it. */
  const selectAgent = (id: string) => {
    setChosenAgentId(id);
    if (conversationId && conversation?.agentId !== id) open(null);
  };

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || turn.isStreaming) return;
    let id = conversationId;
    if (!id) {
      if (!agent) return;
      try {
        id = (await createConversation({ agentId: agent.id, appKey }).unwrap())
          .id;
      } catch (error) {
        const notInApp = errorCodeOf(error) === AGENT_NOT_IN_APP;
        // The agent was taken out of this app meanwhile: the list is refreshed, the user picks again.
        if (notInApp) dispatch(agentsApi.util.invalidateTags(['Agents']));
        setStartError(
          notInApp
            ? 'Цей агент більше не доступний тут — оберіть іншого.'
            : 'Не вдалося почати розмову. Спробуйте ще раз.',
        );
        return;
      }
      setConversationId(id);
    }
    setStartError(null);
    await turn.send(id, trimmed);
  };

  /** «Повторити» under a failed answer sends the last question again. */
  const retryLast = (failed: ChatMessage) => {
    const last = [...messages]
      .reverse()
      .find(m => m.role === 'user' && m.id !== failed.id);
    if (last) void send(last.text);
  };

  return {
    conversationId,
    conversation,
    isLoadingConversation: conversationQuery.isLoading,
    open,
    agent,
    selectAgent,
    messages,
    turn,
    send,
    retryLast,
    startError,
  };
};

export type AgentChat = ReturnType<typeof useAgentChat>;
